import { createElement, use } from "react";
import { declaredLang, loadChoice, loadVisited, resolveLanguage } from "@/i18n/language";
import { translate } from "@/i18n/strings";

/**
 * Une page chargee a la demande, dans son propre fichier JS.
 *
 * Pourquoi pas React.lazy : le prerendu (scripts/ssr.cjs) rend les pages avec
 * renderToStaticMarkup, qui n'attend pas une page en cours de chargement et
 * ecrirait une page vide a sa place. ssr.cjs remplace donc chaque import() par
 * un require() synchrone, et ce composant sait rendre sur-le-champ une page
 * dont le chargeur a rendu le module lui-meme plutot qu'une promesse — ce que
 * React.lazy refuse.
 *
 * Aucun <Suspense> ne doit entourer ces pages. Sans frontiere, React 19
 * suspend la racine entiere :
 * - au premier affichage, le HTML prerendu reste a l'ecran tant que le code
 *   de la page n'est pas arrive, au lieu d'etre remplace par un repli vide ;
 * - a la navigation, que BrowserRouter applique dans une Transition, l'ancienne
 *   page reste affichee, puis le fondu part une fois la nouvelle prete.
 * Une frontiere ajoutee plus haut afficherait son repli dans les deux cas.
 */

const RELOAD_KEY = "dartsync:chunk-reload";
const RELOAD_WINDOW_MS = 30_000;

const pages = [];

// Une promesse qui ne se resout jamais : React garde affiche ce qui l'est deja.
const forever = () => new Promise(() => {});

/**
 * Apres une mise en production, un onglet reste ouvert demande les fichiers
 * de l'ancien build, qui n'existent plus : Caddy lui repond app.html, et
 * webpack echoue a charger la page. On recharge alors l'adresse visee, deja
 * poussee dans l'historique par le routeur, ce qui sert le nouveau build.
 *
 * Un second echec sur la meme adresse dans les 30 s n'est plus un build
 * perime : on abandonne en laissant la page affichee telle quelle, avec un
 * bandeau qui propose de recharger. Faire remonter l'erreur serait pire —
 * sans frontiere d'erreur, React viderait la racine, HTML prerendu compris. Sans sessionStorage pour s'en souvenir, on
 * abandonne des le premier echec : recharger bouclerait sans fin.
 */
function reloadOnce(error) {
    if (typeof window === "undefined") throw error;
    const target = window.location.pathname;
    try {
        const last = JSON.parse(sessionStorage.getItem(RELOAD_KEY) || "null");
        if (!last || last.path !== target || Date.now() - last.at >= RELOAD_WINDOW_MS) {
            sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ path: target, at: Date.now() }));
            window.location.reload();
            return forever();
        }
    } catch {
        // Stockage indisponible : abandon, voir plus haut.
    }
    console.error("[lazyPage] page introuvable, abandon apres rechargement :", error);
    showFailure(target);
    return forever();
}

/**
 * Le bandeau d'abandon, pose dans le document et non rendu par React.
 *
 * Le Toaster de l'application ne suffit pas : sur /game/<id> ouvert a froid,
 * la racine n'a encore rien affiche quand la page echoue, le Toaster n'est
 * donc pas monte et l'ecran resterait blanc sans un mot. Ici le bandeau
 * s'affiche dans tous les cas. La langue suit la meme regle que l'interface,
 * sans le choix d'origine que seul le fournisseur de langue connait.
 */
export const FAILURE_ID = "page-load-failure";

function showFailure(path) {
    if (document.getElementById(FAILURE_ID)) return;
    const lang = resolveLanguage({
        routeLang: declaredLang(path),
        choice: loadChoice(),
        visitedLang: loadVisited(),
    });

    const banner = document.createElement("div");
    banner.id = FAILURE_ID;
    banner.setAttribute("role", "alert");
    banner.className =
        "fixed left-1/2 top-4 z-[100] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#141414] px-4 py-3 text-sm text-white shadow-lg";

    const message = document.createElement("span");
    message.textContent = translate(lang, "load.failed");

    const button = document.createElement("button");
    button.type = "button";
    button.className = "shrink-0 rounded-lg bg-brand-red px-3 py-1.5 font-semibold text-white hover:bg-[#c72027]";
    button.textContent = translate(lang, "load.reload");
    button.addEventListener("click", () => window.location.reload());

    banner.append(message, button);
    document.body.append(banner);
}

export function lazyPage(load) {
    let loaded = null;
    let pending = null;

    function preload() {
        if (loaded) return Promise.resolve(loaded);
        if (!pending) {
            const result = load();
            if (typeof result?.then !== "function") {
                loaded = result;
                return Promise.resolve(loaded);
            }
            const promise = result.then(
                (module) => {
                    // Marque la promesse comme React le fait lui-meme : use() lit
                    // alors la valeur sans suspendre, y compris dans
                    // renderToStaticMarkup, qui ne sait pas attendre.
                    promise.status = "fulfilled";
                    promise.value = module;
                    return (loaded = module);
                },
                // La promesse reste en attente, jamais rejetee ni remplacee : une
                // nouvelle promesse a chaque rendu fait boucler React sur la
                // racine, qui abandonne au centieme essai en vidant la page.
                (error) => reloadOnce(error),
            );
            pending = promise;
        }
        return pending;
    }

    function LazyPage(props) {
        if (!loaded && !pending) preload();
        // use() a chaque rendu tant qu'une promesse existe, meme resolue : React
        // refuse un composant qui l'appelle pour suspendre puis ne l'appelle
        // plus au rendu qui reprend.
        const module = pending ? use(pending) : loaded;
        return createElement(module.default, props);
    }

    LazyPage.preload = preload;
    pages.push(LazyPage);
    return LazyPage;
}

/** Charge toutes les pages : les tests rendent l'arbre de routes d'un bloc. */
export function preloadAllPages() {
    return Promise.all(pages.map((page) => page.preload()));
}
